import React, { ReactNode, useEffect, useRef } from 'react';
import { Disclosure } from '@headlessui/react';
import useClassNames from '@/helpers/hooks/useClassNames';
import AccordionButton from './AccordionButton';
import { ChevronDownIcon } from '@heroicons/react/24/solid';

export interface AccordionProps {
  index?: number;
  variant?: 'arrow' | 'plusAndMinus';
  accordionListLength?: number;
  className?: string;
  openSectionTitle?: string;
  closedSectionTitle?: string;
  iconClassName?: string;
  buttonClassName?: string;
  buttonWrapperClassName?: string;
  panelClassName?: string;
  collapsedLabel?: string;
  customOpenButton?: ReactNode;
  customClosedButton?: ReactNode;
  onClick?: () => void;
  defaultOpen?: boolean;
  forceToggle?: number;
  forceState?: boolean;
  onStateChange?: (isOpen: boolean) => void;
}

const DisclosureController: React.FC<{ forceToggle?: number; forceState?: boolean; open: boolean; onStateChange?: (isOpen: boolean) => void }> = ({ forceToggle, forceState, open, onStateChange }) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const prevToggle = useRef(forceToggle);
  const prevOpen = useRef(open);
  
  useEffect(() => {
    if (forceToggle !== undefined && forceToggle !== prevToggle.current) {
      if (forceState !== undefined && forceState !== open) {
        buttonRef.current?.click();
      }
      prevToggle.current = forceToggle;
    }
  }, [forceToggle, forceState, open]);

  useEffect(() => {
    if (open !== prevOpen.current) {
      prevOpen.current = open;
      onStateChange?.(open);
    }
  }, [open, onStateChange]);

  return <Disclosure.Button ref={buttonRef} className="hidden" />;
};

const AccordionPanel: React.FC<{
  open: boolean;
  className?: string;
  children: ReactNode;
}> = ({ open, className, children }) => {
  const panelClasses = useClassNames([
    className,
    'overflow-hidden',
    'transition-all duration-150 ease-out',
    'origin-top transform-gpu',
    open
       ? 'scale-y-100 opacity-100 max-h-none overflow-visible'
      : 'scale-y-95 opacity-0 max-h-0 overflow-hidden',
  ]);

  return (
    <Disclosure.Panel className={panelClasses} unmount={false}>
      {children}
    </Disclosure.Panel>
  );
};

const Accordion = React.forwardRef<
  HTMLDivElement,
  React.PropsWithChildren<AccordionProps>
>(
  (
    {
      variant = 'arrow',
      closedSectionTitle,
      openSectionTitle = closedSectionTitle,
      children,
      className = '',
      iconClassName = '',
      buttonClassName = '',
      buttonWrapperClassName = '',
      panelClassName = '',
      collapsedLabel,
      customClosedButton,
      customOpenButton = customClosedButton,
      defaultOpen = false,
      forceToggle,
      forceState,
      onStateChange,
      onClick,
    },
    ref
  ) => {
    return (
      <div className={className} onClick={onClick} ref={ref}>
        <Disclosure defaultOpen={defaultOpen}>
          {({ open }) => (
            <div>
              <DisclosureController forceToggle={forceToggle} forceState={forceState} open={open} onStateChange={onStateChange} />
              {customOpenButton || customClosedButton ? (
                <Disclosure.Button className={buttonClassName}>
                  {open ? customOpenButton : customClosedButton}
                  <ChevronDownIcon
                    className={`!w-17 !h-17 ${open ? 'rotate-180' : ''}`}
                  />
                </Disclosure.Button>
              ) : (
                <AccordionButton
                  open={open}
                  variant={variant}
                  collapsedLabel={collapsedLabel}
                  buttonClassName={buttonClassName}
                  iconClassName={iconClassName}
                  openSectionTitle={openSectionTitle}
                  closedSectionTitle={closedSectionTitle || openSectionTitle}
                  buttonWrapperClassName={buttonWrapperClassName}
                />
              )}

              <AccordionPanel open={open} className={panelClassName}>
                {children}
              </AccordionPanel>
            </div>
          )}
        </Disclosure>
      </div>
    );
  }
);

Accordion.displayName = 'Accordion';

export default Accordion;
